import React, { useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import { Host } from "../../../../App";

export default function Wood_Schedule_Page({ postList, onSchedule }) {
  const token = window.localStorage.getItem("acessToken");
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [employees, setEmployees] = useState([]);
  const [Tentative, setTentative] = useState([]);
  const [allData, setAllData] = useState([]);

  useEffect(() => {
    Axios.get(`https://${Host}/api/employees_all/${token}`)
      .then((d) => setEmployees(d.data || []))
      .catch(() => setEmployees([]));
  }, [token]);

  useEffect(() => {
    Axios.get(`https://${Host}/api/Tentative_schedule/${token}`)
      .then((d) => setTentative(d.data || []))
      .catch(() => setTentative([]));
  }, [token]);

  useEffect(() => {
    Axios.get(`https://${Host}/realapi/Production/daily_data_records`, {
      params: { token },
    })
      .then((d) => setAllData(d.data || []))
      .catch(() => setAllData([]));
  }, [token]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Print
  // ─────────────────────────────────────────────────────────────────────────────
  const printRef = useRef(null);
  const handlePrint = () => {
    if (!printRef.current) return;
    const win = window.open("", "", "height=900,width=1200");
    win.document.write("<html><head><title>Print -W Required By Date</title>");
    win.document.write('<link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css"/>');
    win.document.write("</head><body>");
    win.document.write(printRef.current.innerHTML);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────────────────────
  const normalise = (s) => (s || "").replace(/\s+/g, " ").trim();

  // Parse "PART NAME -W (123)" — greedy so names with " - " work
  const parseWEntry = (entry) => {
    const s = normalise(entry);
    const match = s.match(/^(.*\s-W)\s*\(\s*(-?\d+)\s*\)$/i);
    if (!match) return null;
    const internalPart = normalise(match[1]);
    if (!/\s-W$/i.test(internalPart)) return null;
    return { internalPart, qty: parseInt(match[2], 10) };
  };

  // Parse "Main Part - PART NAME -W (N)" from Pending_Parts_List
  const parsePendingW = (str) => {
    const map = {};
    const s = normalise(str || "");
    const entries = s
      .split(/\)\s*,\s*/)
      .map((e, i, arr) => (i < arr.length - 1 ? e + ")" : e));

    entries.forEach((entry) => {
      const stripped = entry.replace(/^[\w\s]+ Part\s*-\s*/i, "");
      const match = stripped.match(/^(.*\s-W)\s*\(\s*(-?\d+)\s*\)$/i);
      if (match) {
        const internalPart = normalise(match[1]);
        const qty = parseInt(match[2], 10);
        if (/-W$/i.test(internalPart))
          map[internalPart.toUpperCase()] = (map[internalPart.toUpperCase()] || 0) + qty;
      }
    });
    return map;
  };

  // Parse PO_Details → [{date, qty}] sorted asc
  const parsePoDetails = (poStr) => {
    if (!poStr) return [];
    return String(poStr)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .reduce((acc, seg) => {
        const m = seg.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*-\s*(-?\d+)/);
        if (m) {
          const qty = Math.max(0, parseInt(m[4], 10));
          if (qty > 0)
            acc.push({
              date: `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`,
              qty,
            });
        }
        return acc;
      }, [])
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 1 — Future tentative (Date >= today) by internal part
  // ─────────────────────────────────────────────────────────────────────────────
  const partAggregates = useMemo(() => {
    const futureTentativeByInternal = {};
    (Tentative || []).forEach((t) => {
      const schedDate = (t.Date || "").slice(0, 10);
      if (schedDate < todayISO) return;
      const key = normalise(
        t.Internal_Part_No || t.internal_part_no || t.internalPartNo || ""
      ).toUpperCase();
      const qty = Math.max(0, Number(t.QTY ?? t.qty ?? 0) || 0);
      if (key && qty > 0)
        futureTentativeByInternal[key] = (futureTentativeByInternal[key] || 0) + qty;
    });

    const unfinishedByInternal = {};
    (allData || []).forEach((row) => {
      const rowDate = (row.Date || row.date || "").slice(0, 10);
      if (rowDate < todayISO) return;
      const key = normalise(
        row.Part_internalNo || row.Internal_Part_No || row.internal_part_no || ""
      ).toUpperCase();
      const remain = Math.max(
        0,
        (Number(row.Target ?? 0) || 0) - (Number(row.Produced ?? 0) || 0)
      );
      if (key && remain > 0)
        unfinishedByInternal[key] = (unfinishedByInternal[key] || 0) + remain;
    });

    return { futureTentativeByInternal, unfinishedByInternal };
  }, [Tentative, allData, todayISO]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 2 — Parse -W shortage rows from postList
  //
  //  SOURCE OF TRUTH: Required_Ship_QTY_List "-W (N)" — never recalculate
  //  DATE PLACEMENT:  PO walk using Pending_Parts_List as available stock
  //
  //  Walk algorithm (same as Rigid):
  //    demandThreshold = totalPO - sqlRequiredQty
  //    Walk PO dates forward — skip covered dates, place shortage on last dates
  // ─────────────────────────────────────────────────────────────────────────────
  const parseRequiredParts = useMemo(() => {
    if (!postList || postList.length === 0) return [];
    const results = [];

    postList.forEach((row) => {
      const reqList = normalise(row.Required_Ship_QTY_List || "");
      if (!reqList || !reqList.includes("-W")) return;

      // Split safely on "), "
      const entries = reqList
        .split(/\)\s*,\s*/)
        .map((e, i, arr) => (i < arr.length - 1 ? e + ")" : e));

      // Build pendingMap from Pending_Parts_List (for date placement only)
      const pendingMap = parsePendingW(row.Pending_Parts_List || "");
      const poEntries = parsePoDetails(row.PO_Details);
      const totalPoQty = poEntries.reduce((s, x) => s + x.qty, 0);

      entries.forEach((entry) => {
        const parsed = parseWEntry(entry);
        if (!parsed || parsed.qty <= 0) return;

        const { internalPart, qty: sqlRequiredQty } = parsed;
        const key = internalPart.toUpperCase();
        const futureTent = partAggregates.futureTentativeByInternal[key] || 0;
        const unfinished = partAggregates.unfinishedByInternal[key] || 0;

        // HOW MANY to schedule — SQL value, adjusted only for tentative/unfinished
        const pendingToSchedule = Math.min(
          sqlRequiredQty,
          Math.max(0, sqlRequiredQty - futureTent + unfinished)
        );

        if (pendingToSchedule <= 0) return;

        if (poEntries.length === 0) {
          results.push({
            internalPart,
            description: row.Part_description || "",
            customer: row.Customer || "",
            requiredQty: sqlRequiredQty,
            shortageQty: pendingToSchedule,
            poDate: null,
            poQty: totalPoQty,
            poDetails: row.PO_Details || "",
            futureTentative: futureTent,
            unfinished,
            sourceRow: row,
          });
          return;
        }

        // PO walk — shortage lands on LAST dates (same as Rigid)
        // demandThreshold = how much PO demand stock already covers
        let demandThreshold = Math.max(0, totalPoQty - sqlRequiredQty);
        let remainingShortage = pendingToSchedule;

        for (const { date: poDate, qty: poQty } of poEntries) {
          if (remainingShortage <= 0) break;

          if (demandThreshold >= poQty) {
            demandThreshold -= poQty;
          } else {
            const uncoveredInThisDate = poQty - demandThreshold;
            const shortageHere = Math.min(uncoveredInThisDate, remainingShortage);
            demandThreshold = 0;

            results.push({
              internalPart,
              description: row.Part_description || "",
              customer: row.Customer || "",
              requiredQty: sqlRequiredQty,
              shortageQty: shortageHere,
              poDate,
              poQty: totalPoQty,
              poDetails: row.PO_Details || "",
              futureTentative: futureTent,
              unfinished,
              sourceRow: row,
            });

            remainingShortage -= shortageHere;
          }
        }
      });
    });

    return results;
  }, [postList, partAggregates]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 3 — Group by PO shortage date
  // ─────────────────────────────────────────────────────────────────────────────
  const groupedByDate = useMemo(() => {
    const groups = {};
    parseRequiredParts.forEach((part) => {
      const dateKey = part.poDate || "No Date";
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(part);
    });
    return Object.fromEntries(
      Object.entries(groups).sort(([a], [b]) => {
        if (a === "No Date") return 1;
        if (b === "No Date") return -1;
        return a.localeCompare(b);
      })
    );
  }, [parseRequiredParts]);

  const dates = useMemo(() => Object.keys(groupedByDate), [groupedByDate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // STEP 4 — Grand totals (de-duplicate requiredQty per part+customer)
  // ─────────────────────────────────────────────────────────────────────────────
  const grandTotals = useMemo(() => {
    const seenReq = new Set();
    let totalRequired = 0;
    let totalFutureTent = 0;
    let totalUnfinished = 0;
    let totalPending = 0;

    parseRequiredParts.forEach((p) => {
      const key = `${p.customer}|${p.internalPart}`;
      if (!seenReq.has(key)) {
        seenReq.add(key);
        totalRequired += p.requiredQty;
        totalFutureTent += p.futureTentative;
        totalUnfinished += p.unfinished;
      }
      totalPending += p.shortageQty;
    });

    return { totalRequired, totalFutureTent, totalUnfinished, totalPending };
  }, [parseRequiredParts]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────
  const alignDesc = (internal) =>
    (internal || "").replace(/\s*-W\s*$/i, "").replace(/\s+/g, " ").trim();

  const isWeekendISO = (iso) => {
    const [y, m, d] = (iso || "").split("-").map(Number);
    if (!y || !m || !d) return false;
    return [0, 6].includes(new Date(y, m - 1, d).getDay());
  };

  const addDaysISO = (iso, days) => {
    const dt = new Date(`${iso}T00:00:00`);
    dt.setDate(dt.getDate() + days);
    return dt.toISOString().slice(0, 10);
  };

  const nextBusinessDayISO = (iso) => {
    let cur = addDaysISO(iso, 1);
    while (isWeekendISO(cur)) cur = addDaysISO(cur, 1);
    return cur;
  };

  const businessSeries = (startISO, count) => {
    let cur = startISO;
    while (isWeekendISO(cur)) cur = addDaysISO(cur, 1);
    const out = [cur];
    for (let i = 1; i < count; i++) { cur = nextBusinessDayISO(cur); out.push(cur); }
    return out;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Scheduling state
  // ─────────────────────────────────────────────────────────────────────────────
  const [showSchedule, setShowSchedule] = useState(false);
  const [sched, setSched] = useState({});

  const setField = (key, field, value) =>
    setSched((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { employee: "", date: "", qty: "", internalPartNo: "" }),
        [field]: value,
      },
    }));

  const tentativeIdsRef = useRef({});
  const perPartCountersRef = useRef({});

  const getTentativeId = (row, suffix = "") => {
    const baseKey = (row.internalPart || "NA").trim() || "NA";
    const mapKey = suffix ? `${baseKey}::${suffix}` : baseKey;
    if (tentativeIdsRef.current[mapKey]) return tentativeIdsRef.current[mapKey];
    perPartCountersRef.current[baseKey] = (perPartCountersRef.current[baseKey] || 0) + 1;
    const ts = new Date().toISOString().replace(/[-:.TZ]/g, "");
    const id = `${baseKey}-${ts}-${perPartCountersRef.current[baseKey]}${suffix ? `-${suffix}` : ""}`;
    tentativeIdsRef.current[mapKey] = id;
    return id;
  };

  const handleRowSchedule = (key, r) => {
    const data = sched[key] || {};
    const chosenInternal = (data.internalPartNo || r.internalPart || "").trim();
    const payload = {
      employee: (data.employee || "").trim(),
      date: (data.date || "").trim(),
      qty: Number(data.qty || 0),
      internalPartNo: chosenInternal,
      description: alignDesc(chosenInternal),
      customer: r.customer,
      poQty: Number(r.poQty || 0),
      shortage: Number(r.shortageQty || 0),
      totalRequired: r.requiredQty,
      Tentative_ID: getTentativeId(r),
      poDate: r.poDate || "",
      type: "-W",
    };
    Axios.post(`https://${Host}/Rigid/tentative/add`, { changedData: JSON.stringify(payload), token })
      .then((res) => alert(res.data?.message || "Saved."))
      .catch((err) => alert(err?.response?.data?.message || err?.message || "Something went wrong."));
    if (onSchedule) onSchedule(payload);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3" style={{ maxHeight: 900, overflowY: "auto" }}>

      {/* ── Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Wood (-W) Parts Requirements</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={handlePrint}>Print</button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowSchedule((s) => !s)}
          >
            {showSchedule ? "Hide Schedule" : "Add Schedule"}
          </button>
        </div>
      </div>

      {showSchedule && employees?.length > 0 && (
        <datalist id="employees">
          {employees.map((e) => <option key={e.Emp_ID || e} value={e.Name || e} />)}
        </datalist>
      )}

      <div ref={printRef}>

        {/* ── Status banner ── */}
        {parseRequiredParts.length === 0 ? (
          <div className="alert alert-warning">
            <strong>No -W parts found with required quantity &gt; 0</strong>
            <div className="mt-2">
              <small>
                {(postList || []).filter((r) =>
                  String(r.Required_Ship_QTY_List || "").includes("-W")
                ).length} rows contain -W in SQL output but all are fully covered.
              </small>
            </div>
          </div>
        ) : (
          <div className="alert alert-success mb-3">
            Showing {parseRequiredParts.length} -W shortage row(s) across {dates.length} PO date(s).
            &nbsp;<small className="text-muted">(Tentative counted from {todayISO} onward only)</small>
          </div>
        )}

        {/* ── Per-date tables ── */}
        {dates.map((date) => {
          const parts = groupedByDate[date];
          if (!parts || parts.length === 0) return null;

          const dateReqTotal = parts.reduce((s, p) => s + p.requiredQty, 0);
          const dateTentTotal = parts.reduce((s, p) => s + p.futureTentative, 0);
          const dateUnfinTotal = parts.reduce((s, p) => s + p.unfinished, 0);
          const datePendingTotal = parts.reduce((s, p) => s + p.shortageQty, 0);

          return (
            <div key={date} className="mb-4">
              <h5 className="mb-2 d-flex align-items-center gap-2">
                {date === "No Date" ? "Parts without PO date" : `PO Date: ${date}`}
                <span className="badge bg-danger" style={{ fontSize: "0.8rem", color: "white" }}>
                  Shortage: {datePendingTotal}
                </span>
              </h5>

              <table className="table table-bordered table-striped table-sm">
                <thead className="table-dark">
                  <tr>
                    <th>Internal Part No</th>
                    <th>Description</th>
                    <th>Customer</th>
                    <th>PO QTY</th>
                    <th>Total Req -W</th>
                    <th title="Future-dated tentative schedules >= today">Fut. Tent. ↓</th>
                    <th title="Today-onward unfinished jobs not yet produced — added back">Unfinished ↑</th>
                    <th title="Shortage on this specific PO date">Pending Sched.</th>
                    {showSchedule && (
                      <>
                        <th>Employee</th>
                        <th>Date</th>
                        <th>QTY</th>
                        <th>Schedule</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {parts.map((r, i) => {
                    const rowKey = `${date}|${r.internalPart}|${i}`;
                    const rowState = sched[rowKey] || {
                      employee: "", date: "", qty: "", internalPartNo: r.internalPart,
                    };
                    return (
                      <tr key={rowKey}>
                        <td>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => {
                              // open schedule row directly
                              setShowSchedule(true);
                            }}
                          >
                            {r.internalPart}
                          </button>
                        </td>
                        <td>{alignDesc(r.internalPart)}</td>
                        <td>{r.customer}</td>
                        <td>{r.poQty}</td>
                        <td><strong>{r.requiredQty}</strong></td>
                        <td>
                          {r.futureTentative > 0
                            ? <span className="badge bg-info text-dark">{r.futureTentative}</span>
                            : <span className="text-muted">0</span>}
                        </td>
                        <td>
                          {r.unfinished > 0
                            ? <span className="badge bg-secondary">{r.unfinished}</span>
                            : <span className="text-muted">0</span>}
                        </td>
                        <td>
                          {r.shortageQty > 0
                            ? <span className="badge bg-warning text-dark fs-6">{r.shortageQty}</span>
                            : <span className="text-success fw-bold">✓ 0</span>}
                        </td>
                        {showSchedule && (
                          <>
                            <td style={{ minWidth: 180 }}>
                              <input
                                className="form-control form-control-sm"
                                placeholder="Select or type"
                                value={rowState.employee}
                                list="employees"
                                onChange={(e) => setField(rowKey, "employee", e.target.value)}
                              />
                            </td>
                            <td style={{ minWidth: 150 }}>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={rowState.date}
                                onChange={(e) => setField(rowKey, "date", e.target.value)}
                              />
                            </td>
                            <td style={{ width: 150 }}>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="form-control form-control-sm"
                                value={rowState.qty}
                                placeholder={String(r.shortageQty || 0)}
                                onChange={(e) => setField(rowKey, "qty", e.target.value)}
                              />
                            </td>
                            <td style={{ width: 120 }}>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleRowSchedule(rowKey, r)}
                              >
                                Schedule
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>

                {/* Sub-total per date */}
                <tfoot>
                  <tr className="table-secondary fw-bold">
                    <td colSpan={4} className="text-end">
                      Subtotal ({date === "No Date" ? "No Date" : date}):
                    </td>
                    <td>{dateReqTotal}</td>
                    <td>{dateTentTotal}</td>
                    <td>{dateUnfinTotal}</td>
                    <td>
                      <span className="badge bg-danger" style={{ fontSize: "0.8rem", color: "white" }}>{datePendingTotal}</span>
                    </td>
                    {showSchedule && <td colSpan={4} />}
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })}

        {/* ── Grand Total ── */}
        {parseRequiredParts.length > 0 && (
          <div className="mb-4">
            <h5 className="mb-2">Grand Total — All -W Parts</h5>
            <table className="table table-bordered table-sm" style={{ maxWidth: 560 }}>
              <thead className="table-dark">
                <tr><th>Metric</th><th className="text-end">QTY</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Required -W Parts (SQL)</td>
                  <td className="text-end fw-bold text-danger">{grandTotals.totalRequired}</td>
                </tr>
                <tr>
                  <td>Future Tentative Scheduled <small className="text-muted">(&ge; {todayISO})</small></td>
                  <td className="text-end fw-bold text-info">{grandTotals.totalFutureTent}</td>
                </tr>
                <tr>
                  <td>Unfinished In-Progress <small className="text-muted">(today+ not yet produced)</small></td>
                  <td className="text-end fw-bold text-secondary">{grandTotals.totalUnfinished}</td>
                </tr>
                <tr className="table-warning">
                  <td>
                    <strong>Total Pending Schedule</strong>
                    <small className="text-muted ms-2">(sum of all date shortages)</small>
                  </td>
                  <td className="text-end fw-bold">{grandTotals.totalPending}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}